import React from 'react';
import { Input } from '../Input/Input';
import { Radio } from '../Radio/Radio';
import { MultiSelect } from '../MultiSelect/MultiSelect';
import { CheckboxGroup } from '../CheckboxGroup/CheckboxGroup';

import { FormikProps } from 'formik';
import { AddUserFormValues } from './types';
import { useFormFields, roleOptions } from './hooks/useFormFields';
import { User } from '../../api/users';

interface FormFieldsProps {
  formik: FormikProps<AddUserFormValues>;
  userDetails?: User | null;
}

export const FormFields: React.FC<FormFieldsProps> = ({ formik, userDetails }) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    agencyOptions,
    isAdmin,
    availableAccessOptions,
    shouldShowOffice,
    officeOptions,
  } = useFormFields(formik, userDetails);

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.setFieldTouched(e.target.name, true, true);
    handleBlur(e);
  };

  return (
    <div className="form-fields">
      <Input
        id="firstName"
        name="firstName"
        label="First Name"
        placeholder="Enter First Name"
        value={values.firstName}
        onChange={handleChange}
        onBlur={handleInputBlur}
        touched={formik.touched.firstName}
        error={formik.errors.firstName}
      />
      <Input
        id="lastName"
        name="lastName"
        label="Last Name"
        placeholder="Enter Last Name"
        value={values.lastName}
        onChange={handleChange}
        onBlur={handleInputBlur}
        touched={formik.touched.lastName}
        error={formik.errors.lastName}
      />
      <Input
        id="email"
        name="email"
        label="Email"
        placeholder="example@gmail.com"
        value={values.email}
        onChange={handleChange}
        onBlur={handleInputBlur}
        touched={formik.touched.email}
        error={formik.errors.email}
      />
      {values.accessTo?.includes('leaderboard') && (
        <Input
          id="phone"
          name="phone"
          label="Phone Number"
          placeholder="(555) 123-4567"
          value={values.phone}
          onChange={handleChange}
          onBlur={handleInputBlur}
          touched={formik.touched.phone}
          error={formik.errors.phone}
          mask="phone"
        />
      )}
      <MultiSelect
        id="agency"
        name="agency"
        label="Agency"
        options={agencyOptions}
        value={values.agency}
        onChange={(newValue) => {
          setFieldValue('agency', newValue);
          formik.setFieldTouched('agency', true, true);
        }}
        error={touched.agency ? (errors.agency as string) : undefined}
        placeholder="Choose Agency"
      />
      {isAdmin && (
        <Radio
          id="role"
          name="role"
          label="User Role"
          options={roleOptions}
          value={values.role}
          onChange={(e) => {
            handleChange(e);
            formik.setFieldTouched('role', true, true);
            if (e.target.value !== 'USER' && values.accessTo.includes('leaderboard')) {
              setFieldValue(
                'accessTo',
                values.accessTo.filter((access) => access !== 'leaderboard')
              );
              setFieldValue('office', []);
            }
          }}
        />
      )}
      <CheckboxGroup
        id="accessTo"
        name="accessTo"
        label="Access to"
        options={availableAccessOptions}
        value={values.accessTo}
        onChange={(newValue) => {
          setFieldValue('accessTo', newValue);
          formik.setFieldTouched('accessTo', true, true);
          if (!newValue.includes('leaderboard')) {
            setFieldValue('office', []);
          }
        }}
        error={touched.accessTo ? (errors.accessTo as string) : undefined}
      />
      {shouldShowOffice && (
        <MultiSelect
          id="office"
          name="office"
          label="Office"
          options={officeOptions}
          value={values.office}
          onChange={(newValue) => {
            setFieldValue('office', newValue);
            formik.setFieldTouched('office', true, true);
          }}
          error={touched.office ? (errors.office as string) : undefined}
          placeholder="Choose Office(s)"
        />
      )}
    </div>
  );
};
